import React, { useCallback, useRef } from "react";
import { DateTimePicker } from "orion-components/CBComponents";
import { useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { useState } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect } from "react";
import EventBus from "orion-components/SharedComponents/EventBus";
import JumpToDifferentTime from "./JumpToDifferentTime/JumpToDifferentTime";
import { Tooltip } from "@mui/material";
import { getRecordedWindow, normalizeRecordedDuration } from "./Utils/videoDateRangeSelector";
import { makeSelectComponentProp } from "orion-components/SharedComponents/ComponentState/redux/selectors";
import { getActualDuration, calcuateDateTimeDifference } from "../../utils";

const datePickerProptypes = {
	params: PropTypes.any,
	dateTime: PropTypes.any
};

const CustomDatePickerInput = ({ params, dateTime }) => {
	return <span {...params}>{dateTime.format("MM/DD/YYYY").toString()}</span>;
};

CustomDatePickerInput.propTypes = datePickerProptypes;

const getDurationInMilliseconds = (toSubtract, type, dateTime) => {
	const result = moment(dateTime).subtract(toSubtract, type);
	return result.valueOf();
};

const usePreviousValue = value => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const getSelectedClipRange = selectedClip => {
	const startMs = new Date(selectedClip?.startTime).getTime();
	const endMs = new Date(selectedClip?.endTime).getTime();

	if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
		return null;
	}

	return { startMs, endMs, key: `${selectedClip?.id || "clip"}:${startMs}:${endMs}` };
};

// This function creates a time window around the given input time.
// The configured duration is treated as the total playback length, split
// around the capture time using the alert recorded-window split rules.
const getTimeWindow = (inputTime, totalDuration = 20, unit = "seconds") => {
	const baseDate = new Date(inputTime);
	const now = new Date();
	const { seconds } = normalizeRecordedDuration(totalDuration, unit);
	const { startISO, endISO } = getRecordedWindow(baseDate, seconds);
	const pastTime = startISO.toDate();
	const futureTime = endISO.toDate();

	const format = date =>
		date.toLocaleString("en-US", {
			month: "2-digit",
			day: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false
		});

	const isCurrent = now >= pastTime && now <= futureTime;

	return {
		from: format(pastTime),
		to: isCurrent ? null : format(futureTime)
	};
};

// Preserve the existing non-alert external playback behavior. It uses a
// millisecond half split and is not part of the Alert Profile time window
const getDefaultRecordedTimeWindow = (inputTime, totalDuration = 20, unit = "seconds") => {
	const baseDate = new Date(inputTime);
	const now = new Date();

	const unitMap = {
		seconds: 1000,
		minutes: 60 * 1000,
		hours: 60 * 60 * 1000
	};

	const totalTime = totalDuration * (unitMap[unit] || unitMap.minutes);
	const halfTime = totalTime / 2;

	const pastTime = new Date(baseDate.getTime() - halfTime);
	const futureTime = new Date(baseDate.getTime() + halfTime);

	const format = date =>
		date.toLocaleString("en-US", {
			month: "2-digit",
			day: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false
		});

	const isCurrent = Math.abs(now.getTime() - baseDate.getTime()) < halfTime;

	return {
		from: format(pastTime),
		to: isCurrent ? null : format(futureTime)
	};
};

const VideoDateRangeSelector = ({
	instanceId,
	duration,
	getStartTime,
	isOnAlertProfile,
	alertDateTime,
	getStartAndEndDateTime,
	getDuration,
	playerType,
	recordedMode,
	initialRecordedDuration,
	initialRecordedTimeUnit,
	setVideoDurationExternally,
	registerSetStartDateExternally,
	formatedDuration,
	formatedDurationUnit,
	onRecordedTimeframeChange,
	selectedClip
}) => {
	const dateTimeDuration = getActualDuration(duration);
	const selectedClipRange = getSelectedClipRange(selectedClip);
	// const locale = useSelector((state) => state.i18n && state.i18n.locale);
	const localeOverride = "en"; // There is an issue with the Arabic locale when used in seekbar calculations. For now, I'm setting it to use only the English locale.
	moment.locale(localeOverride);

	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	const format = timeFormatPreference ? `full_${timeFormatPreference}` : "full_12-hour";
	const dir = useSelector(state => getDir(state));
	const initialDateTime = selectedClipRange ? moment(selectedClipRange.endMs) : moment().subtract(0, "minutes");
	const [dateTime, setDateTime] = useState(initialDateTime);
	const startDateValue = selectedClipRange
		? moment(selectedClipRange.startMs)
		: calcuateDateTimeDifference(dateTimeDuration.value, dateTimeDuration.type, initialDateTime);
	const endDateValue = initialDateTime;
	const [startDate, setStartDate] = useState(startDateValue);
	const [endDate, setEndDate] = useState(endDateValue);
	const [isDateSelectedByUser, setIsDateSelectedByUser] = useState(false);
	const [displayJumpToTimeMenuFor, setDisplayJumpToTimeMenuFor] = useState("");
	const [position, setPosition] = useState("left");
	const jumpToTimeRef = useRef(null);
	const isDateTimeForRef = useRef(null);
	const initializedClipKeyRef = useRef(null);
	const lastPublishedNativeRangeRef = useRef(null);
	const prevStartDate = usePreviousValue(startDate);

	useEffect(() => {
		if (!selectedClipRange || initializedClipKeyRef.current === selectedClipRange.key) {
			return;
		}

		const startMoment = moment(selectedClipRange.startMs);
		const endMoment = moment(selectedClipRange.endMs);
		isDateTimeForRef.current = "startDate";
		setStartDate(startMoment);
		setEndDate(endMoment);
		setDateTime(endMoment);
	}, [selectedClipRange?.key]);

	// 1) Handle alert-profile initialization only when those inputs change
	useEffect(() => {
		if (!isOnAlertProfile) return;
		const { seconds: recordedDurationSec } = normalizeRecordedDuration(
			initialRecordedDuration,
			initialRecordedTimeUnit
		);
		setUpAlertDateTime(moment(alertDateTime), recordedDurationSec);
		// do NOT depend on startDate here
	}, [isOnAlertProfile, alertDateTime, initialRecordedDuration, initialRecordedTimeUnit]);

	// 2) When NOT on alert profile, notify parent only when startDate changes
	useEffect(() => {
		if (isOnAlertProfile) return;
		if (startDate !== prevStartDate) {
			getStartTime(startDate.toDate());
		}
	}, [isOnAlertProfile, startDate, prevStartDate, getStartTime]);

	const setUpAlertDateTime = (date, recordedDurationSec) => {
		const { startISO, endISO } = getRecordedWindow(date, Number(recordedDurationSec));
		// Normalize to moment first, then to ms
		const startMs = moment(startISO).valueOf();
		const endMs = moment(endISO).valueOf();

		// Keep your state as moments for formatting in the UI
		setDateTime(date);
		setStartDate(startISO);
		setEndDate(endISO);

		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal: startMs,
					endDateTimeVal: endMs,
					recordedMode
				});
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal: startMs,
					endDateTimeVal: endMs,
					recordedMode
				});
				break;
			case "flashphoner":
				EventBus.publish("PlaybackRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					startDateTimeVal: startMs,
					endDateTimeVal: endMs
				});
				break;
			case "native":
			case "ws-jpeg":
				EventBus.publish("Universal_PlaybackCommand", {
					instanceId,
					command: "setCursor",
					recordedMode,
					time: startMs, // ms
					duration: duration * 60 // seconds (as required by ws-jpeg)
				});
				break;
			default:
				console.error("Unknown camera system");
				return;
		}

		getStartTime(new Date(startISO));
		getDuration(duration);
		getStartAndEndDateTime(startMs, endMs); // both ms
	};
	const handleSubmit = (date, isUserTriggered = false, dateTimeDurVal = null, dateTimeDurType = null) => {
		setDateTime(date);
		setStartDate(
			calcuateDateTimeDifference(
				dateTimeDurVal === null ? dateTimeDuration.value : dateTimeDurVal,
				dateTimeDurType === null ? dateTimeDuration.type : dateTimeDurType,
				date
			)
		);
		setEndDate(date);

		const startDateTimeVal = getDurationInMilliseconds(
			dateTimeDurVal === null ? dateTimeDuration.value : dateTimeDurVal,
			dateTimeDurType === null ? dateTimeDuration.type : dateTimeDurType,
			date
		);
		let endDateTimeVal = null;
		if (dateTimeDuration.type === "minutes") {
			endDateTimeVal = new Date(startDateTimeVal + dateTimeDuration.value * 60 * 1000);
		} else {
			endDateTimeVal = new Date(startDateTimeVal + dateTimeDuration.value * 60 * 60 * 1000);
		}
		if (isOnAlertProfile) {
			getStartTime(new Date(startDateTimeVal));
		}

		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal,
					endDateTimeVal: endDateTimeVal.getTime(),
					recordedMode
				});
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal,
					endDateTimeVal: endDateTimeVal.getTime(),
					recordedMode
				});
				break;
			case "flashphoner":
				EventBus.publish("PlaybackRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					startDateTimeVal,
					endDateTimeVal: endDateTimeVal.getTime()
				});
				break;
			case "native":
			case "ws-jpeg":
				EventBus.publish("Universal_PlaybackCommand", {
					instanceId,
					command: "setCursor",
					time: startDateTimeVal
				});
				break;
			default:
				console.error("Unknown camera system");
				return;
		}

		if (isUserTriggered) {
			setIsDateSelectedByUser(!isDateSelectedByUser);
			onRecordedTimeframeChange?.(
				new Date(startDateTimeVal).toISOString(),
				endDateTimeVal.toISOString()
			);
		}
	};

	const componentState = useSelector(
		makeSelectComponentProp(COMPONENT_KEY, instanceId, "components.recorded.videoDateRangeSelector")
	);

	const setPlaybackVideoDateTime = () => {
		let startDateTimeVal = null;
		let enddDateTimeVal = new Date();
		if (componentState?.setDateTimeExternally) {
			const configuredDuration = Number.isNaN(parseInt(initialRecordedDuration, 10))
				? 20
				: parseInt(initialRecordedDuration, 10);
			const getExternalTimeWindow = isOnAlertProfile ? getTimeWindow : getDefaultRecordedTimeWindow;
			const { from, to } = getExternalTimeWindow(
				componentState?.playbackCaptureTime,
				configuredDuration,
				initialRecordedTimeUnit
			);
			const startMoment = moment(from);
			const endMoment = moment(to);

			setStartDate(startMoment);
			setEndDate(endMoment);
			setDateTime(endMoment);

			startDateTimeVal = startMoment;
			enddDateTimeVal = new Date(endMoment);

			getStartAndEndDateTime(startMoment, endMoment);
		} else {
			if (isDateTimeForRef.current === "startDate") {
				startDateTimeVal = startDate;
				enddDateTimeVal = new Date(endDate);
				getStartAndEndDateTime(startDate, endDate);
			} else {
				const dateTimeDuration = getActualDuration(duration);
				startDateTimeVal = calcuateDateTimeDifference(dateTimeDuration.value, dateTimeDuration.type, dateTime);
				enddDateTimeVal = new Date(endDateValue);
				getStartAndEndDateTime(startDateTimeVal, endDateValue);
			}
		}

		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal: new Date(startDateTimeVal).getTime(),
					endDateTimeVal: enddDateTimeVal.getTime(),
					recordedMode
				});
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal: new Date(startDateTimeVal).getTime(),
					endDateTimeVal: enddDateTimeVal.getTime(),
					recordedMode
				});
				break;
			case "flashphoner":
				EventBus.publish("PlaybackRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					startDateTimeVal: new Date(startDateTimeVal).getTime(),
					endDateTimeVal: enddDateTimeVal.getTime()
				});
				break;
			case "native": {
				const startMs = new Date(startDateTimeVal).getTime();
				const endMs = enddDateTimeVal.getTime();
				if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
					return;
				}

				if (selectedClipRange && initializedClipKeyRef.current !== selectedClipRange.key) {
					const rangeMatchesClip =
						startMs === selectedClipRange.startMs && endMs === selectedClipRange.endMs;
					if (!rangeMatchesClip) {
						return;
					}
					initializedClipKeyRef.current = selectedClipRange.key;
				}

				const rangeKey = `${startMs}:${endMs}`;
				if (lastPublishedNativeRangeRef.current === rangeKey) {
					return;
				}
				lastPublishedNativeRangeRef.current = rangeKey;

				EventBus.publish("Universal_PlaybackCommand", {
					instanceId,
					command: "setCursor",
					time: startMs,
					duration: (endMs - startMs) / 1000
				});
				break;
			}
			case "ws-jpeg":
				EventBus.publish("Universal_PlaybackCommand", {
					instanceId,
					command: "setCursor",
					time: startDateTimeVal,
					duration: duration * 60 // duration is in minutes, convert to seconds
				});
				break;
			default:
				console.error("Unknown camera system");
				return;
		}
	};

	useEffect(() => {
		if (!isOnAlertProfile) {
			setPlaybackVideoDateTime();
		}
	}, [
		startDate,
		duration,
		isOnAlertProfile,
		componentState?.playbackCaptureTime,
		componentState?.setDateTimeExternally
	]);

	const setStartDateExternally = useCallback(
		val => {
			if (val.instanceId !== instanceId) return;

			const { startDateTimeVal, endDateTimeVal, isFor, userTriggered } = val;
			const externalStartMoment = startDateTimeVal ? moment(new Date(startDateTimeVal)) : null;
			const externalEndMoment = endDateTimeVal ? moment(new Date(endDateTimeVal)) : null;

			if (externalStartMoment?.isValid() && externalEndMoment?.isValid()) {
				isDateTimeForRef.current = "startDate";

				setStartDate(externalStartMoment);
				setEndDate(externalEndMoment);
				setDateTime(isFor === "startDate" ? externalStartMoment : externalEndMoment);

				const startTime = externalStartMoment.toDate();
				const endTime = externalEndMoment.toDate();
				getStartTime(startTime);
				getStartAndEndDateTime(startTime.getTime(), endTime.getTime());
				if (userTriggered) {
					onRecordedTimeframeChange?.(startTime.toISOString(), endTime.toISOString());
				}

				return;
			}

			if (isFor === "startDate") {
				isDateTimeForRef.current = "startDate";

				const startMoment = moment(new Date(startDateTimeVal));
				const endMoment = endDateTimeVal ? moment(new Date(endDateTimeVal)) : null;

				setDateTime(startMoment);
				setStartDate(startMoment);
				if (endMoment) setEndDate(endMoment);
				getStartTime(startMoment.toDate());

				return;
			}

			// isFor === "endDate"
			isDateTimeForRef.current = "endDate";

			const endMoment = moment(new Date(endDateTimeVal));
			const startMoment = startDateTimeVal ? moment(new Date(startDateTimeVal)) : null;

			setDateTime(endMoment);
			if (startMoment) {
				setStartDate(startMoment);
				getStartTime(startMoment.toDate()); // keep .toDate() if your impl expects Date
			} else {
				// optional: clear or keep previous start
				// setStartDate(null);
			}
			setEndDate(endMoment);
		},
		[getStartAndEndDateTime, getStartTime, instanceId, onRecordedTimeframeChange]
	);

	useEffect(() => {
		// Register callback for parent
		if (registerSetStartDateExternally) {
			registerSetStartDateExternally(setStartDateExternally);
		}
	}, [registerSetStartDateExternally, setStartDateExternally]);

	const openJumptoDifferentTime = name => {
		jumpToTimeRef.current.getTime(name === "startDate" ? startDate : endDate);
		setDisplayJumpToTimeMenuFor(name);
	};

	return (
		<div style={{ position: "absolute", bottom: 23, width: "100%", height: "100%" }} id="bcc_dateRangeSelector">
			<div
				style={{
					display: "flex",
					height: "100%",
					width: "100%",
					justifyContent: "space-between",
					alignItems: "center"
				}}
			>
				<div
					style={{
						...(dir === "ltr" && { float: "left", marginLeft: "0.3%" }),
						...(dir === "rtl" && { float: "right", marginRight: "0.3%" }),
						height: 13,
						width: 96,
						background: "#1D1D1F",
						borderRadius: 2,
						fontSize: 10,
						overflow: "hidden",
						whiteSpace: "nowrap"
					}}
				>
					<Tooltip
						title={startDate.format("MM/DD/YYYY HH:mm:ss").toString()}
						placement="top"
						disableHoverListener={!isOnAlertProfile}
						disableFocusListener={!isOnAlertProfile}
						disableTouchListener={!isOnAlertProfile} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-video-date-range-selector-tooltip"
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								height: "100%",
								fontSize: "1em",
								color: "#fff"
							}}
							onClick={() => {
								openJumptoDifferentTime("startDate");
								setPosition("left");
							}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-video-date-range-selector-div"
						>
							{startDate.format(isOnAlertProfile ? "HH:mm:ss" : "MM/DD/YYYY HH:mm:ss").toString()}
						</div>
					</Tooltip>
				</div>

				<div
					style={{
						background: "#1D1D1F",
						width: 60,
						height: 13,
						borderRadius: 2,
						fontSize: 10
					}}
					id="bcc_date_selector"
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
							height: "100%",
							fontSize: "1em",
							color: "#fff",
							padding: "0px 4px"
						}}
					>
						<DateTimePicker
							id="VideoDateRangeSelector"
							label=""
							value={dateTime}
							handleChange={date => handleSubmit(date, true)}
							maxDate={new Date()}
							format={format}
							dir={dir}
							RenderInput={CustomDatePickerInput}
							getUpdatedDateOnSubmit={true}
						/>
					</div>
				</div>

				<div
					style={{
						...(dir === "ltr" && { float: "right", marginLeft: "0.3%" }),
						...(dir === "ltr" && { float: "left", marginRight: "0.3%" }),
						height: 13,
						width: 95,
						borderRadius: 2,
						background: "#1D1D1F",
						fontSize: 10
					}}
				>
					<Tooltip
						title={endDate.format("MM/DD/YYYY HH:mm:ss").toString()}
						placement="top"
						disableHoverListener={!isOnAlertProfile}
						disableFocusListener={!isOnAlertProfile}
						disableTouchListener={!isOnAlertProfile} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-video-date-range-selector-tooltip-2"
					>
						<div
							onClick={() => {
								openJumptoDifferentTime("endDate");
								setPosition("right");
							}}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								height: "100%",
								fontSize: "1em",
								color: "#fff",
								overflow: "hidden",
								whiteSpace: "nowrap"
							}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-video-date-range-selector-div-2"
						>
							{endDate.format(isOnAlertProfile ? "HH:mm:ss" : "MM/DD/YYYY HH:mm:ss").toString()}
						</div>
					</Tooltip>
				</div>
			</div>
			<JumpToDifferentTime
				instanceId={instanceId}
				open={displayJumpToTimeMenuFor}
				closePopup={() => {
					setDisplayJumpToTimeMenuFor("");
				}}
				position={position}
				isDateTimeFor={displayJumpToTimeMenuFor}
				setStartDateExternally={setStartDateExternally}
				setVideoDurationExternally={setVideoDurationExternally}
				ref={jumpToTimeRef}
				formatedDuration={formatedDuration}
				formatedDurationUnit={formatedDurationUnit}
			/>
		</div>
	);
};

const areEqualByProps = keys => (prevProps, nextProps) => {
	return keys.every(key => Object.is(prevProps[key], nextProps[key]));
};

VideoDateRangeSelector.propTypes = {
	instanceId: PropTypes.string,
	duration: PropTypes.number,
	getStartTime: PropTypes.func,
	isOnAlertProfile: PropTypes.bool,
	alertDateTime: PropTypes.any,
	getStartAndEndDateTime: PropTypes.func,
	getDuration: PropTypes.func,
	playerType: PropTypes.string,
	recordedMode: PropTypes.string,
	initialRecordedDuration: PropTypes.string,
	initialRecordedTimeUnit: PropTypes.string,
	setVideoDurationExternally: PropTypes.func,
	registerSetStartDateExternally: PropTypes.func,
	formatedDuration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	formatedDurationUnit: PropTypes.string,
	onRecordedTimeframeChange: PropTypes.func,
	selectedClip: PropTypes.object
};

export default React.memo(
	VideoDateRangeSelector,
	areEqualByProps([
		"isOnAlertProfile",
		"instanceId",
		"duration",
		"recordedMode",
		"alertDateTime",
		"playerType",
		"initialRecordedDuration",
		"initialRecordedTimeUnit",
		"formatedDuration",
		"formatedDurationUnit",
		"onRecordedTimeframeChange",
		"selectedClip"
	])
);
